import * as _ from "underscore";
import { REPORT_NAMES } from "./enum";

// Downtime graph only shows these reasons (frontend restriction). Edit or extend this list to change it.
export const DOWNTIME_GRAPH_REASONS = [
    {key: 'MACHINE_BREAKDOWN', label: 'Machine Breakdown'},
    {key: 'CYLINDER_BREAKDOWN', label: 'Cylinder Breakdown'},
    {key: 'NO_OPERATOR', label: 'No Operator'},
];

const UNIT = {
    PERCENT: '%',
    METER: ' m',
    KG: ' kg',
    MINUTE: ' min',
    NONE: '',
};

const PERCENT_AXIS = {min: 0, max: 100, padding: {top: 0, bottom: 0}};

const toNumber = (value) => {
    const number = Number(value);
    return value !== null && value !== undefined && Number.isFinite(number) ? number : 0;
};

const clampPercent = (value) => Math.min(Math.max(toNumber(value), 0), 100);

const formatValue = (value, unit) => toNumber(value).toFixed(2) + unit;

// Axis ticks stay integer-only; tooltips get their own formatter so decimals never render as null
const integerTick = {
    format: function (d) {
        return (parseInt(d) === d) ? d : null;
    },
};

/**
 * Builds C3 data + tooltip from series definitions.
 * Each series: {name, values (plotted), raw (shown in tooltip, defaults to values), unit, color}
 */
const buildGraph = (series, dataOptions = {}) => {
    const colors = {};
    const rawValues = {};
    const units = {};

    series.forEach(s => {
        if (s.color) {
            colors[s.name] = s.color;
        }
        rawValues[s.name] = s.raw || s.values;
        units[s.name] = s.unit;
    });

    const data = {
        columns: series.map(s => [s.name].concat(s.values)),
        type: 'bar',
        colors,
        ...dataOptions,
    };

    const tooltip = {
        format: {
            value: function (value, ratio, id, index) {
                const raw = rawValues[id] && rawValues[id][index] !== undefined ? rawValues[id][index] : value;
                return formatValue(raw, units[id] || UNIT.NONE);
            },
        },
    };

    return {data, tooltip};
};

// Percentages are plotted capped to 0-100 while the tooltip keeps the real value
const percentSeries = (name, values, color) => ({
    name,
    values: values.map(clampPercent),
    raw: values.map(toNumber),
    unit: UNIT.PERCENT,
    color,
});

const unionKeys = (objects) => {
    const keys = [];
    objects.forEach((r) => {
        Object.keys(r || {}).forEach((k) => {
            keys.includes(k) === false && keys.push(k);
        });
    });
    return keys;
};

export const generateGraphStructure=(jsonData,performance, multiGraph = null)=> {

    const month = _.pluck(jsonData, 'key');

    let graph = {};
    let yLabel;
    let yAxis = {};
    let y2Axis;

    if(performance===REPORT_NAMES.PERFORMANCE){
        graph = buildGraph([
            percentSeries('Performance', _.pluck(jsonData, 'performance'), '#003847'),
        ]);
        yLabel="Performance [%]";
        yAxis = PERCENT_AXIS;
    }

    if(performance===REPORT_NAMES.ACHIEVEMENT){
        const achievements = _.pluck(jsonData, performance).map(a => a || {});
        graph = buildGraph([
            {name: 'Target Output', values: _.pluck(achievements, 'targetOutput').map(toNumber), unit: UNIT.METER, color: '#003847'},
            {name: 'Actual Output', values: _.pluck(achievements, 'output').map(toNumber), unit: UNIT.METER, color: '#23A396'},
            percentSeries('Achievement %', _.pluck(achievements, 'achievement'), '#F29C1F'),
        ], {
            types: {'Achievement %': 'line'},
            axes: {'Achievement %': 'y2'},
        });
        yLabel="Output [m]";
        y2Axis = {
            show: true,
            ...PERCENT_AXIS,
            label: {
                text: 'Achievement [%]',
                position: 'outer-middle',
            },
            tick: integerTick,
        };
    }

    if(performance===REPORT_NAMES.DOWNTIME){
        const reasons = _.pluck(jsonData, "reasons").map(r => r || {});
        const series = DOWNTIME_GRAPH_REASONS.map(reason => ({
            name: reason.label,
            values: reasons.map(r => toNumber(r[reason.key])),
            unit: UNIT.MINUTE,
        }));

        graph = buildGraph(series, {groups: [series.map(s => s.name)]});
        yLabel="Downtime [Minutes]";
    }

    if(performance===REPORT_NAMES.OEE){
        const oee = _.pluck(jsonData, performance).map(o => o || {});
        graph = buildGraph([
            percentSeries('OEE', _.pluck(oee, 'oee'), '#003847'),
        ]);
        yLabel="OEE [%]";
        yAxis = PERCENT_AXIS;
    }

    if(performance===REPORT_NAMES.AVERAGE_ORDER_SIZE){
        if(multiGraph && multiGraph === "orderSize"){
            graph = buildGraph([
                {name: 'Data', values: _.pluck(jsonData, performance), unit: UNIT.METER, color: '#003847'},
            ]);
            yLabel="Average Order Size";

        } else if (multiGraph && multiGraph === "colors"){
            const colors = _.pluck(jsonData, "noOfColorsAndJobs");
            graph = buildGraph(unionKeys(colors).map(k => ({
                name: k,
                values: _.pluck(colors, k).map(value => value || 0),
                unit: UNIT.NONE,
            })));
            yLabel="Colors";

        } else if (multiGraph && multiGraph === "length"){
            const length = _.pluck(jsonData, "lengthAndNoOfJobs");
            graph = buildGraph(unionKeys(length).map(k => ({
                name: k,
                values: _.pluck(length, k).map(value => value || 0),
                unit: UNIT.NONE,
            })));
            yLabel="Length";
        }
    }

    if(performance===REPORT_NAMES.WASTAGE){
        graph = buildGraph([
            {name: 'Actual Wastage', values: _.pluck(jsonData, 'actualWastage').map(toNumber), unit: UNIT.KG, color: '#003847'},
            {name: 'Target Wastage', values: _.pluck(jsonData, 'targetWastage').map(toNumber), unit: UNIT.KG, color: '#33ADAE'},
        ]);
        yLabel="Wastage [Kg]";
    }

    if(performance===REPORT_NAMES.UTILIZATION){
        const machineUtil = _.pluck(jsonData, "machineUtilization").map(m => m || {});

        if(multiGraph && multiGraph === "capacity"){
            // Utilized + lost stack up to the target capacity
            const lostCapacity = _.pluck(machineUtil, 'lostCapacity').map(toNumber);
            graph = buildGraph([
                {name: 'Utilized Capacity', values: _.pluck(machineUtil, 'utilizedOutput').map(toNumber), unit: UNIT.METER, color: '#003847'},
                {name: 'Lost Capacity', values: lostCapacity.map(v => Math.max(0, v)), raw: lostCapacity, unit: UNIT.METER, color: '#23A396'},
            ], {
                groups: [['Utilized Capacity', 'Lost Capacity']],
            });
            yLabel="Capacity [m]";

        } else if (multiGraph && multiGraph === "time"){
            graph = buildGraph([
                percentSeries('Time Utilization', _.pluck(machineUtil, 'time'), '#003847'),
            ]);
            yLabel="Time Utilization [%]";
            yAxis = PERCENT_AXIS;
        }
    }

    const axis = {
        x: {
            show: true,
            type: 'category',
            categories:month,
            tick: {
                multiline: true,
                rotate:window.innerWidth > 767 ? 0 : 9,
                height: 100,
            },
            label: {
                text: '',
                position: 'outer-center',
            },

        },
        y: {
            ...yAxis,
            label: {
                text: yLabel,
                position: 'outer-middle',
            },
            tick: integerTick,
        },
    };

    if (y2Axis) {
        axis['y2'] = y2Axis;
    }

    const zoom = {
        rescale: true
    };

    const graphData = {};
    graphData['data'] = graph.data;
    graphData['axis'] = axis;
    graphData['zoom'] = zoom;
    graphData['tooltip'] = graph.tooltip;

    return graphData;
}
